import { useCallback, useMemo, useState, useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '@/shared/ui/dialog';
import type { OrganizationOverviewData } from '@/shared/types/overviewTypes';
import { Activity, Cable, ChevronDown, SatelliteDish, X } from 'lucide-react';
import { AccountDetailsTab } from '@/shared/config/account-details-tabs';
import { DataConsumptionSection } from '@/domains/global-dashboard/sections/consumption/DataConsumptionSection';
import { FleetOverviewSection } from '@/domains/global-dashboard/sections/service-lines/FleetOverviewSection';
import { TerminalInventorySection } from '@/domains/global-dashboard/sections/terminals/TerminalInventorySection';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { cn } from '@/shared/utils/date-utils';
import { SectionType, buildAccountChartColorMap } from '@/domains/global-dashboard/utils/chart-utils';
import { useRouteNavigation } from '@/shared/hooks/use-route-navigation';
import { User } from '@/shared/types/userTypes';

export type GlobalDashboardDialogProps = {
  currentUser: User;
  organizations: OrganizationOverviewData[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/** Full-screen global dashboard shell (markets selector + tabs). Consumers own `open` / close behavior. */
export function GlobalDashboardDialog({ currentUser, organizations, open, onOpenChange }: GlobalDashboardDialogProps) {
  const { switchOrganizationAndNavigateToAccountDetails } = useRouteNavigation();
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<Set<string>>(new Set());
  const [marketsOpen, setMarketsOpen] = useState(false);

  const marketName = useMemo(() => {
    return organizations?.length === 1 ? organizations[0].name : undefined;
  }, [organizations]);

  useEffect(() => {
    if (!organizations?.length) {
      setSelectedOrganizationIds(new Set());
      return;
    }
    setSelectedOrganizationIds((prev) => {
      const valid = [...prev].filter((id) => organizations.some((a) => a.organizationId === id));
      if (valid.length === 0) {
        const ids =
          organizations.length <= 5 ? organizations.map((a) => a.organizationId) : organizations.slice(0, 5).map((a) => a.organizationId);
        return new Set(ids);
      }
      return new Set(valid.slice(0, 5));
    });
  }, [organizations]);

  const sortedOrganizations = useMemo(() => {
    if (!organizations?.length) return [];
    return organizations.sort((a, b) => a.name.localeCompare(b.name));
  }, [organizations]);

  const accountChartColorById = useMemo(() => {
    if (!sortedOrganizations?.length) return new Map();
    return buildAccountChartColorMap(sortedOrganizations);
  }, [sortedOrganizations]);

  const selectedOrganizations = useMemo(() => {
    if (!sortedOrganizations?.length) return [];
    return sortedOrganizations?.filter((o) => selectedOrganizationIds.has(o.organizationId)).slice(0, 5);
  }, [sortedOrganizations, selectedOrganizationIds]);

  const selectedMarketsOrdered = useMemo(() => {
    if (!sortedOrganizations?.length) return [];
    return sortedOrganizations?.filter((o) => selectedOrganizationIds.has(o.organizationId));
  }, [sortedOrganizations, selectedOrganizationIds]);

  const marketsTriggerLabel = useMemo(() => {
    if (selectedMarketsOrdered?.length === 0) return 'Select markets';
    return `Currently Selected ${selectedMarketsOrdered?.length} Markets`;
  }, [selectedMarketsOrdered]);

  const navigateToOrganization = useCallback(
    async (organizationId: string, sectionType: SectionType) => {
      if (!organizationId) return;
      const organization = currentUser.organizations?.find((a) => a.organizationId === organizationId);
      if (!organization) return;
      switchOrganizationAndNavigateToAccountDetails({
        organization, accountDetailsTab: sectionType === SectionType.TERMINAL_INVENTORY ? AccountDetailsTab.TERMINALS : AccountDetailsTab.DASHBOARD
      });
    },
    [currentUser, switchOrganizationAndNavigateToAccountDetails],
  );

  const toggleMarketSelection = useCallback((id: string, checked: boolean) => {
    setSelectedOrganizationIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        if (next.size >= 5) return prev;
        next.add(id);
      } else {
        if (next.size <= 1) return prev;
        next.delete(id);
      }
      return next;
    });
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-[90] bg-black/60 backdrop-blur-[2px] duration-300 ease-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=open]:animate-in" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-4 z-[100] flex flex-col gap-0 overflow-hidden rounded-xl border bg-background p-0 shadow-xl outline-none',
            'duration-300 ease-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=open]:animate-in',
          )}
          aria-labelledby="global-dashboard-title"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogPrimitive.Close
            type="button"
            className="absolute right-4 top-4 z-[101] rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
            aria-label="Close dashboard"
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>
          <div className="relative shrink-0 space-y-1 border-b bg-background px-4 py-4 pr-14">
            <div className="flex flex-row items-start justify-between gap-4">
              <div className="min-w-0 space-y-1">
                <DialogTitle id="global-dashboard-title" className="text-left text-2xl font-bold tracking-tight">
                  {organizations?.length > 1 ? 'Global Dashboard' : `Market Dashboard - ${marketName}`}
                </DialogTitle>
                <p className="text-sm sm:text-base">
                  {organizations?.length > 1 ? 'Cross-market data consumption, service lines, and terminals overview for the selected markets.' : 'Data consumption, service lines, and terminals overview for the selected market.'}
                </p>
              </div>
              {organizations?.length > 1 && (
                <Popover open={marketsOpen} onOpenChange={setMarketsOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={organizations?.length === 0}
                      className={cn('min-w-[14rem] shrink-0 justify-between font-normal')}
                    >
                      <span className="truncate text-left">{marketsTriggerLabel}</span>
                      <ChevronDown className="ml-2 shrink-0 opacity-60" aria-hidden />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="z-[110] w-80 p-3" align="end">
                    <p className="text-sm font-medium mb-1">Markets in view</p>
                    <p className="text-xs mb-3">
                      Choose up to five markets ({selectedOrganizationIds.size}/5). Charts follow this selection.
                    </p>
                    <div className="flex flex-col gap-2 max-h-[min(320px,50vh)] overflow-y-auto pr-1">
                      {sortedOrganizations?.map((o) => {
                        const dot = accountChartColorById.get(o.organizationId);
                        const checked = selectedOrganizationIds.has(o.organizationId);
                        const atCap = selectedOrganizationIds.size >= 5 && !checked;
                        return (
                          <label
                            key={o.organizationId}
                            className={cn(
                              'flex items-center gap-2 text-sm cursor-pointer select-none rounded-md px-1 py-1 hover:bg-muted/60',
                              atCap && 'opacity-50 cursor-not-allowed',
                            )}
                          >
                            <span
                              className="h-3.5 w-3.5 rounded-sm shrink-0 ring-1 ring-border/70"
                              style={{ backgroundColor: dot }}
                              aria-hidden
                            />
                            <Checkbox
                              checked={checked}
                              disabled={atCap}
                              onCheckedChange={(c) => toggleMarketSelection(o.organizationId, c === true)}
                            />
                            <span className="truncate">{o.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4 flex flex-col">
            <Tabs defaultValue="fleet" className="flex flex-col space-y-4">
              <TabsList className="grid w-full max-w-2xl grid-cols-3 shrink-0 justify-center mx-auto">
                <TabsTrigger value="fleet" className="data-[state=active]:bg-foreground data-[state=active]:text-background flex items-center gap-2 justify-center">
                  <Cable className="h-4 w-4" />
                  Service Lines
                </TabsTrigger>
                <TabsTrigger value="data" className="data-[state=active]:bg-foreground data-[state=active]:text-background flex items-center gap-2 justify-center">
                  <Activity className="h-4 w-4" />
                  Data Consumption
                </TabsTrigger>
                <TabsTrigger value="inventory" className="data-[state=active]:bg-foreground data-[state=active]:text-background flex items-center gap-2 justify-center">
                  <SatelliteDish className="h-4 w-4" />
                  Terminals Inventory
                </TabsTrigger>
              </TabsList>
              <TabsContent value="fleet">
                <FleetOverviewSection
                  selectedOrganizations={selectedOrganizations}
                  accountChartColorById={accountChartColorById}
                  onNavigateToOrganization={navigateToOrganization}
                />
              </TabsContent>
              <TabsContent value="data">
                <DataConsumptionSection
                  selectedOrganizations={selectedOrganizations}
                  accountChartColorById={accountChartColorById}
                  onNavigateToOrganization={navigateToOrganization}
                />
              </TabsContent>
              <TabsContent value="inventory">
                <TerminalInventorySection
                  selectedOrganizations={selectedOrganizations}
                  accountChartColorById={accountChartColorById}
                  onNavigateToOrganization={navigateToOrganization}
                />
              </TabsContent>
            </Tabs>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}